import cmpConfig from "../cmp-config";
import {log} from "../util/log";
import {TCString} from "../tcstring/t-c-string";
import {AddtlConsent} from "../addtl-consent/addtl-consent";
import {site} from "../context/context";
import {EProviderType} from "services-comun-meteored/modules/services/cmp/interface";
import {CustomConsent} from "../custom-consent/custom-consent";

interface IPurpose {
    id: number;
    name: string;
    description: string;
    illustrations?: string[];
}

interface ISpecialPurpose {
    id: number;
    name: string;
    description: string;
    illustrations?: string[];
}

interface IFeature {
    id: number;
    name: string;
    description: string;
    illustrations?: string[];
}

interface ISpecialFeature {
    id: number;
    name: string;
    description: string;
    illustrations?: string[];
}

interface IStack {
    id:number;
    purposes:number[];
    specialFeatures:number[];
    name:string;
    description:string;
}

interface IStacks {
    [key:number]:IStack;
}

interface IDataCategories {
    [key:number]: IDataCategory;
}

export interface IDataCategory {
    id: number;
    name: string;
    description: string;
}

interface IOverflow {
    httpGetLimit:number;
}

interface IDataRetention {
    stdRetention?: number;
    purposes?: {
        [key:number]:number;
    },
    specialPurposes?: {
        [key:number]:number;
    }
}

export interface IUrl {
    langId: string;
    privacy: string;
    legIntClaim?: string;
}

interface IVendor {
    id:number;
    name:string;
    purposes:number[];
    legIntPurposes:number[];
    flexiblePurposes:number[];
    specialPurposes:number[];
    features:number[];
    specialFeatures:number[];
    policyUrl:string;
    cookieMaxAgeSeconds: number|null;
    usesNonCookieAccess: boolean;
    dataRetention?: IDataRetention;
    urls?: IUrl[];
    dataDeclaration?: number[];
    deviceStorageDisclosureUrl?: string;
    overflow?:IOverflow;
    deletedDate?:string;
}

interface IPurposes {
    [key:number]:IPurpose;
}

interface ISpecialPurposes {
    [key:number]:ISpecialPurpose;
}

interface IFeatures {
    [key:number]:IFeature;
}

interface ISpecialFeatures {
    [key:number]:ISpecialFeature;
}

interface IVendors {
    [key:number]:IVendor;
}

interface IAdditionalVendor {
    id:number;
    name:string;
    policy_url:string;
}

interface IAdditionalVendors {
    [key:number]:IAdditionalVendor;
}

interface ICustomVendors {
    [key:number]: ICustomVendor;
}

export interface ICustomVendor {
    id: number;
    name: string;
    privacy_url: string;
    types: EProviderType[];
}

interface IVendorList {
    gvlSpecificationVersion: number;
    vendorListVersion: number;
    tcfPolicyVersion: number;
    lastUpdated: string;
    purposes: IPurposes;
    specialPurposes: ISpecialPurposes;
    features: IFeatures;
    specialFeatures:ISpecialFeatures;
    stacks: IStacks;
    dataCategories: IDataCategories;
    vendors: IVendors;
    additionalVendors: IAdditionalVendors;
    customVendors?: ICustomVendors;
}

class VendorList {

    /* STATIC */

    /* INSTANCE */

    private _vendorList: IVendorList;
    private _loaded: boolean = false;
    private _vendorsLIIds: number[]|null = null;

    public constructor() {
        this._vendorList = {
            gvlSpecificationVersion: 0,
            vendorListVersion: 0,
            tcfPolicyVersion: 0,
            lastUpdated: '',
            purposes: {},
            specialPurposes: {},
            features: {},
            specialFeatures: {},
            stacks: {},
            dataCategories: {},
            vendors: {},
            additionalVendors: {},
            customVendors: {}
        };
    }

    public proxyLoad(tcString: TCString, addtlConsent: AddtlConsent, customConsent?: CustomConsent): void {
        log.debug('Loading Proxy Vendor List...');
        this._vendorsLIIds = [];
        this._vendorList.gvlSpecificationVersion = 2;
        this._vendorList.vendorListVersion = tcString.coreString().vendorListVersion;
        this._vendorList.tcfPolicyVersion = tcString.coreString().tcfPolicyVersion;
        this._vendorList.lastUpdated = new Date(tcString.coreString().lastUpdated).toString();
        this._vendorList.purposes = {};
        Object.keys(tcString.purposes)
            .map(purposeId => parseInt(purposeId))
            .filter(purposeId => tcString.purposes[purposeId] !== undefined)
            .forEach(puporseId => {
                this._vendorList.purposes[puporseId] = {
                    id: puporseId,
                    name: '',
                    description: '',
                    illustrations: []
                }
            });
        this._vendorList.specialPurposes = {};
        this._vendorList.features = {};
        this._vendorList.specialFeatures = {};
        Object.keys(tcString.purposes)
            .map(featureId => parseInt(featureId))
            .filter(featureId => tcString.specialFeatures[featureId] !== undefined)
            .forEach(featureId => {
                this._vendorList.specialFeatures[featureId] = {
                    id: featureId,
                    name: '',
                    description: '',
                    illustrations: []
                };
            });
        this._vendorList.stacks = {};
        this._vendorList.vendors = {};
        Object.keys(tcString.vendors)
            .map((vendorId:string) => {return parseInt(vendorId);})
            .filter((vendorId:number) => {return tcString.vendors[vendorId];})
            .forEach((vendorId:number) => {
                this._vendorList.vendors[vendorId] = {
                    id: vendorId,
                    name: '',
                    policyUrl: '',
                    features: [],
                    flexiblePurposes: [],
                    legIntPurposes: Object.keys(this._vendorList.purposes).map((purpose:string) => {return parseInt(purpose);}).filter((purposeId:number) => {return purposeId != 1;}),
                    purposes: Object.keys(this._vendorList.purposes).map((purpose:string) => {return parseInt(purpose);}),
                    specialFeatures: Object.keys(this._vendorList.specialFeatures).map((feature:string) => {return parseInt(feature);}),
                    specialPurposes: [],
                    cookieMaxAgeSeconds: null,
                    usesNonCookieAccess: false,
                    dataDeclaration: [],
                };
            });
        this._vendorList.additionalVendors = {};

        if (addtlConsent) {
            addtlConsent.vendorsIds().forEach((vendorId:number) => {
                this._vendorList.additionalVendors[vendorId] = {
                    id: vendorId,
                    name: '',
                    policy_url: ''
                };
            });
        }

        if (customConsent) {
            customConsent.vendorsIds().forEach(vendorId => {
                (this._vendorList.customVendors??={})[vendorId] = {
                    id: vendorId,
                    name: '',
                    privacy_url: '',
                    types: []
                };
            });
        }

        // Cargamos los Ids de Vendors de LI guardados en la tcString
        if (tcString.vendorsLI) {
            Object.keys(tcString.vendorsLI)
                .forEach((vendorIdStr:string) => {
                        const vendorId:number = parseInt(vendorIdStr);
                        if (tcString.vendorsLI[vendorId] === true) this._vendorsLIIds?.push(vendorId);
                    }
                );
        }

        this._loaded = true;
        log.debug('Vendor List loaded');
    }

    /**
     * Carga la lista de vendors.
     */
    public load() : Promise<void> {
        log.debug('Loading Full Vendor List...');
        return new Promise<void>((resolve:Function, reject:Function) => {

            this._vendorsLIIds = null;
            const vendorListUrl:string = `${cmpConfig.vendorListJSONUrl.replace('$$lang$$', site.language)}?hash=${site.hash}`;
            fetch(vendorListUrl, {
                headers: {
                    'Referrer-Policy': 'origin'
                }
            }).then(response => {
                response.json().then((dataJSON: any) => { // TODO - Crear interfaz
                    if (dataJSON.ok
                        && dataJSON.data
                        && dataJSON.data.respuesta
                        && dataJSON.data.respuesta.cmp
                        && dataJSON.data.respuesta.cmp[0]
                        && dataJSON.data.respuesta.cmp[0].gvl) {
                        this._vendorList = dataJSON.data.respuesta.cmp[0].gvl;
                        this._loaded = true;
                        log.debug('Vendor List loaded');
                        resolve();
                    } else {
                        log.debug('Vendor List error loading');
                        reject('Not found');
                    }
                }).catch(err => {
                    reject(err);
                });
            }).catch(err => {
                reject(err);
            });
        });
    }

    /**
     * Recupera los vendors activos.
     * @private
     * @return IVendor[] lista de vendors activos.
     */
    private activeVendors() : IVendor[]
    {
        const now:number = Date.now();
        return Object.values(this._vendorList.vendors).filter((vendor:IVendor) => {
            if (vendor.deletedDate) {
                const deletedDate:Date = new Date(vendor.deletedDate);
                return deletedDate.getTime() > now;
            } else {
                return true;
            }
        });
    }

    /**
     * Comprueba si un vendor en concreto está activo.
     * @param vendorId Id del vendor.
     * @return true si el vendor está activo, false en otro caso.
     * @private
     */
    private isVendorActive(vendorId:number) : boolean
    {
        const vendor:IVendor = this._vendorList.vendors[vendorId];
        if (!vendor) return false;
        if (!vendor.deletedDate) return true;
        return (new Date(vendor.deletedDate)).getTime() > Date.now();
    }

    /**
     * Devuelve un vendor.
     * El Vendor de existir y estar activo.
     * @param vendorId Id del vendor.
     * @return devuelve el vendor si está activo o null en otro caso.
     * @private
     */
    private getVendor(vendorId:number) : IVendor|null
    {
        if (this.isVendorActive(vendorId)) {
            return this._vendorList.vendors[vendorId];
        }
        return null;
    }

    /**
     * Devuelve los IDs de los distintos vendors.
     */
    public vendorIds() : number[]
    {
        return this.activeVendors().map((vendor:IVendor) => {return vendor.id;});
    }

    /**
     * Devuelve los IDs de los distintos vendors que tienen propósitos de LI.
     */
    public vendorLIIds() : number[]
    {
        if (this._vendorsLIIds != null) {
            return this._vendorsLIIds;
        }
        return this.activeVendors()
            .filter((vendor:IVendor) => {return vendor.legIntPurposes.length > 0;})
            .map((vendor:IVendor) => {return vendor.id;})
        ;
    }

    /**
     * Devuelve los IDs de los distintos propósitos.
     */
    public purposesIds() : number[]
    {
        return Object.keys(this._vendorList.purposes).map(key => this._vendorList.purposes[parseInt(key)].id);
    }

    /**
     * Devuelve los IDs de los distintos propósitos especiales.
     */
    public specialPurposesIds() : number[]
    {
        return Object.keys(this._vendorList.specialPurposes).map(key => this._vendorList.specialPurposes[parseInt(key)].id);
    }

    /**
     * Devuelve los IDs de los distintas características.
     */
    public featuresIds() : number[]
    {
        return Object.keys(this._vendorList.features).map(key => this._vendorList.features[parseInt(key)].id);
    }

    /**
     * Devuelve los IDs de los distintas características especiales.
     */
    public specialFeaturesIds() : number[]
    {
        return Object.keys(this._vendorList.specialFeatures).map(key => this._vendorList.specialFeatures[parseInt(key)].id);
    }

    /**
     * Devuelve los IDs de los distintos vendors adicionales fuera de la IAB.
     */
    public additionalVendorsIds() : number[] {
        return Object.keys(this._vendorList.additionalVendors).map((vendorKey:string) => {return parseInt(vendorKey);});
    }

    /**
     * Devuelve los IDs de los distintos vendors personalizados.
     */
    public customVendorsIds() : number[] {
        return Object.keys(this._vendorList.customVendors??{}).map((vendorKey:string) => {return parseInt(vendorKey);});
    }

    /**
     * Versión de la lista.
     */
    public get version() : number|undefined {
        return this._loaded ? this._vendorList.vendorListVersion : undefined;
    }

    /**
     * Versión de la política TCF de la lista.
     */
    public get policyVersion() : number {
        return this._vendorList.tcfPolicyVersion;
    }

    /**
     * Devuelve los propósitos LI de un vendor.
     * @param vendorId Id del vendor a consultar.
     * @return IPurpose[] Array de propósitos LI de un vendor.
     */
    public vendorLIPurposes(vendorId:number) : IPurpose[] {
        const vendor: IVendor|null = this.getVendor(vendorId);
        if (!vendor) {
            return [];
        }

        return Object.values(this._vendorList.purposes).filter((purpose:IPurpose) => {
            return vendor.legIntPurposes.indexOf(purpose.id) >= 0;
        });
    }

    /**
     * Devuelve los distintos propósitos de LI.
     * @return IPurpose[] Array de propósitos LI distintos.
     */
    public purposesLIIds() : number[] {
        const purposeIdx:IPurposes = {};
        this.vendorIds().forEach((vendorId:number) => {
            this.vendorLIPurposes(vendorId).forEach((purpose) => {
                if (!purposeIdx[purpose.id]) {
                    purposeIdx[purpose.id] = purpose;
                }
            });
        });
        return Object.keys(purposeIdx).map((purposeIdStr:string) => {return parseInt(purposeIdStr)});
    }

    /**
     * Devuelve un array de propósitos de la GVL.
     */
    public get purposes() : IPurpose[] {
        return Object.values(this._vendorList.purposes);
    }

    /**
     * Devuelve un array de propósitos especiales de la GVL.
     */
    public get specialPurposes() : ISpecialPurposes[] {
        return Object.values(this._vendorList.specialPurposes);
    }

    /**
     * Recupera información de un vendor.
     * @param vendorId Id del vendor
     */
    public getVendorInfo(vendorId:number) : IVendor|null {
        return this.getVendor(vendorId);
    }

    /**
     * Recupera información de un propósito.
     * @param purposeId Id del propósito
     */
    public getPurposeInfo(purposeId:number) : IPurpose {
        return this._vendorList.purposes[purposeId];
    }

    /**
     * Recupera información de un propósito especial.
     * @param purposeId Id del propósito
     */
    public getSpecialPurposeInfo(purposeId:number) : ISpecialPurpose {
        return this._vendorList.specialPurposes[purposeId];
    }

    /**
     * Recupera información de una característica.
     * @param featureId Id de la característica
     */
    public getFeatureInfo(featureId:number) : IFeature {
        return this._vendorList.features[featureId];
    }

    /**
     * Recupera información de una característica especial.
     * @param featureId Id de la característica
     */
    public getSpecialFeatureInfo(featureId:number) : IFeature {
        return this._vendorList.specialFeatures[featureId];
    }

    /**
     * Recupera información de un vendor adicional externo.
     * @param vendorId Id del vendor
     */
    public getAdditionalVendorInfo(vendorId:number) : IAdditionalVendor {
        return this._vendorList.additionalVendors[vendorId];
    }

    /**
     * Recupera información de un vendor personalizado.
     * @param vendorId Id del vendor
     */
    public getCustomVendorInfo(vendorId: number): ICustomVendor {
        return (this._vendorList.customVendors as ICustomVendors)[vendorId];
    }

    /**
     * Recupera información de una categoría de datos.
     * @param dataCategoryId Id de la categoría de datos
     */
    public getDataCategoryInfo(dataCategoryId: number): IDataCategory {
        return this._vendorList.dataCategories[dataCategoryId];
    }
}

export {VendorList, IPurpose, ISpecialPurpose, IFeature, ISpecialFeature, IVendor, IAdditionalVendor}
